import React, { useState, useEffect } from 'react';
import {
  Ticket as TicketIcon,
  Plus,
  Search,
  Building2,
  Calendar,
  User,
  ArrowRight,
  SlidersHorizontal,
  LayoutGrid,
  List,
  ChevronDown,
  Edit2,
  AlertCircle,
  Briefcase,
  Hash,
} from 'lucide-react';
import { TicketItem, TicketStatus } from '../types/index.ts';

interface TicketsPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const TicketsPage: React.FC<TicketsPageProps> = ({ onNavigate }) => {
  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TicketItem | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [form, setForm] = useState({
    ticketNumber: '',
    complaintNumber: '',
    branchId: '',
    category: 'Branch Renovation & Civil Works',
    subject: '',
    description: '',
    priority: 'MEDIUM',
    assignedTo: 'Engr. Bilal Hashmi',
    targetDate: '',
    remarks: '',
    vendorId: '4',
    vendorName: 'Naeem Builder',
    vendorContactPerson: 'Naeem Taj',
  });

  const [editForm, setEditForm] = useState({
    id: 0,
    ticketNumber: '',
    complaintNumber: '',
    branchId: '',
    category: '',
    subject: '',
    description: '',
    priority: 'MEDIUM',
    assignedTo: '',
    targetDate: '',
    remarks: '',
    status: 'NEW',
    vendorId: '',
    vendorName: '',
    vendorContactPerson: '',
  });

  const statuses: TicketStatus[] = [
    'NEW',
    'ASSIGNED',
    'SITE_SURVEY',
    'BOQ',
    'QUOTATION',
    'APPROVAL',
    'PO_RECEIVED',
    'WORK_IN_PROGRESS',
    'COMPLETED',
    'CERTIFICATE',
    'INVOICE',
    'PAYMENT',
    'CLOSED',
  ];

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const [tRes, bRes, vRes] = await Promise.all([
        fetch('/api/tickets'),
        fetch('/api/branches'),
        fetch('/api/vendors'),
      ]);
      if (tRes.ok) {
        const tData = await tRes.json();
        if (Array.isArray(tData)) setTickets(tData);
      }
      if (bRes.ok) {
        const bData = await bRes.json();
        if (Array.isArray(bData) && bData.length > 0) {
          setBranches(bData);
          setForm((prev) => ({ ...prev, branchId: prev.branchId || String(bData[0].id) }));
        }
      }
      if (vRes.ok) {
        const vData = await vRes.json();
        if (Array.isArray(vData) && vData.length > 0) {
          setVendors(vData);
        }
      }
    } catch (err) {
      console.warn('Tickets fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    try {
      const selectedVendor = vendors.find((v) => String(v.id) === String(form.vendorId));
      const payload = {
        ...form,
        vendorName: selectedVendor ? selectedVendor.name : form.vendorName,
        vendorContactPerson: selectedVendor ? selectedVendor.contactPerson : form.vendorContactPerson,
      };

      const res = await fetch('/api/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.error || 'Failed to create ticket');
        return;
      }

      setShowAddModal(false);
      setForm({
        ticketNumber: '',
        complaintNumber: '',
        branchId: branches[0]?.id ? String(branches[0].id) : '',
        category: 'Branch Renovation & Civil Works',
        subject: '',
        description: '',
        priority: 'MEDIUM',
        assignedTo: 'Engr. Bilal Hashmi',
        targetDate: '',
        remarks: '',
        vendorId: '4',
        vendorName: 'Naeem Builder',
        vendorContactPerson: 'Naeem Taj',
      });
      fetchTickets();
    } catch (err: any) {
      setErrorMsg(err.message || 'Network error occurred');
    }
  };

  const openEditModal = (t: TicketItem) => {
    setSelectedTicket(t);
    setEditForm({
      id: t.id,
      ticketNumber: t.ticketNumber,
      complaintNumber: t.complaintNumber || '',
      branchId: String(t.branchId),
      category: t.category,
      subject: t.subject,
      description: t.description || '',
      priority: t.priority,
      assignedTo: t.assignedTo || '',
      targetDate: t.targetDate || '',
      remarks: t.remarks || '',
      status: t.status,
      vendorId: t.vendorId ? String(t.vendorId) : '4',
      vendorName: t.vendorName || 'Naeem Builder',
      vendorContactPerson: t.vendorContactPerson || 'Naeem Taj',
    });
    setErrorMsg(null);
    setShowEditModal(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    try {
      const selectedVendor = vendors.find((v) => String(v.id) === String(editForm.vendorId));
      const payload = {
        ...editForm,
        vendorName: selectedVendor ? selectedVendor.name : editForm.vendorName,
        vendorContactPerson: selectedVendor ? selectedVendor.contactPerson : editForm.vendorContactPerson,
      };

      const res = await fetch(`/api/tickets/${editForm.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.error || 'Failed to update ticket');
        return;
      }

      setShowEditModal(false);
      fetchTickets();
    } catch (err: any) {
      setErrorMsg(err.message || 'Network error occurred');
    }
  };

  const handleStatusChange = async (ticketId: number, newStatus: string) => {
    try {
      const res = await fetch(`/api/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setTickets((prev) =>
          prev.map((t) => (t.id === ticketId ? { ...t, status: newStatus as TicketStatus } : t))
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const filteredTickets = (Array.isArray(tickets) ? tickets : []).filter((t) => {
    const matchesSearch =
      (t.ticketNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.complaintNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.subject || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.branchName && t.branchName.toLowerCase().includes(search.toLowerCase())) ||
      (t.branchCode && String(t.branchCode).toLowerCase().includes(search.toLowerCase())) ||
      (t.vendorName && t.vendorName.toLowerCase().includes(search.toLowerCase())) ||
      (t.vendorContactPerson && t.vendorContactPerson.toLowerCase().includes(search.toLowerCase()));

    const matchesStatus = filterStatus === 'ALL' || t.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'MEDIUM':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Maintenance &amp; Project Tickets
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200">
              {tickets.length} Registered
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            HERE4U Service Channel • Customer: United Bank Limited (UBL) • Service Provider: Naeem Builder (Naeem Taj)
          </p>
        </div>

        <button
          onClick={() => {
            setErrorMsg(null);
            setShowAddModal(true);
          }}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors self-start sm:self-auto cursor-pointer"
        >
          <Plus className="w-4 h-4 text-amber-400" />
          <span>New HERE4U Ticket</span>
        </button>
      </div>

      {/* Filter and View mode switcher */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-2xs">
        <div className="flex items-center gap-2 w-full sm:max-w-md">
          <Search className="w-4 h-4 text-slate-400 ml-1" />
          <input
            type="text"
            className="w-full text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden"
            placeholder="Search complaint # (e.g. 130631), ticket #, branch 2628, subject..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <select
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-slate-50 text-slate-700 focus:outline-hidden"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="ALL">All Statuses</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, ' ')}
              </option>
            ))}
          </select>

          <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md cursor-pointer ${
                viewMode === 'list' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500'
              }`}
              title="Table View"
            >
              <List className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`p-1.5 rounded-md cursor-pointer ${
                viewMode === 'cards' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500'
              }`}
              title="Cards View"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-2xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-600">
              <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4">Ticket / Complaint</th>
                  <th className="py-3 px-4">Branch &amp; City</th>
                  <th className="py-3 px-4">Subject &amp; Category</th>
                  <th className="py-3 px-4">Service Provider (Vendor)</th>
                  <th className="py-3 px-4">Priority</th>
                  <th className="py-3 px-4">Workflow Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTickets.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-bold text-blue-900">{t.ticketNumber}</div>
                      {t.complaintNumber && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 mt-0.5">
                          <Hash className="w-2.5 h-2.5" /> {t.complaintNumber}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-800">{t.branchName}</div>
                      <div className="text-[11px] text-slate-500 font-mono">Code: {t.branchCode} • {t.city}</div>
                    </td>
                    <td className="py-3 px-4 max-w-sm">
                      <div className="font-semibold text-slate-900 truncate">{t.subject}</div>
                      <div className="text-[11px] text-slate-500 truncate">{t.category}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-800">{t.vendorName || 'Naeem Builder'}</div>
                      <div className="text-[10px] text-slate-500">Contact: {t.vendorContactPerson || 'Naeem Taj'}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${getPriorityBadge(t.priority)}`}>
                        {t.priority}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={t.status}
                        onChange={(e) => handleStatusChange(t.id, e.target.value)}
                        className="text-[11px] font-bold bg-slate-50 border border-slate-300 rounded px-2 py-1 text-slate-800 focus:outline-hidden"
                      >
                        {statuses.map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 px-4 text-right space-x-1.5">
                      <button
                        onClick={() => openEditModal(t)}
                        className="text-xs font-semibold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded transition-colors inline-flex items-center gap-1 cursor-pointer"
                        title="Edit Ticket Details"
                      >
                        <Edit2 className="w-3 h-3 text-slate-500" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => onNavigate('projects')}
                        className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 px-2.5 py-1 rounded transition-colors cursor-pointer"
                      >
                        View Project
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Card / Grid View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTickets.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-slate-200 shadow-2xs p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-xs text-blue-900">{t.ticketNumber}</span>
                  {t.complaintNumber && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      #{t.complaintNumber}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${getPriorityBadge(t.priority)}`}>
                  {t.priority}
                </span>
              </div>

              <div>
                <h3 className="font-bold text-sm text-slate-900 line-clamp-1">{t.subject}</h3>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</p>
              </div>

              <div className="pt-2 border-t border-slate-100 text-xs space-y-1">
                <div className="flex items-center gap-1.5 text-slate-600">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-medium">{t.branchName} ({t.branchCode})</span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-600">
                  <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                  <span>{t.vendorName || 'Naeem Builder'} • {t.vendorContactPerson || 'Naeem Taj'}</span>
                </div>
              </div>

              <div className="pt-2 flex items-center justify-between border-t border-slate-50">
                <select
                  value={t.status}
                  onChange={(e) => handleStatusChange(t.id, e.target.value)}
                  className="text-[11px] font-bold bg-slate-100 border border-slate-200 rounded px-2 py-1 text-slate-800"
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditModal(t)}
                    className="p-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded cursor-pointer"
                    title="Edit Ticket"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onNavigate('projects')}
                    className="text-xs font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-1 cursor-pointer ml-1"
                  >
                    <span>Project</span>
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Ticket Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">Create Manual HERE4U Ticket</h3>
                <p className="text-xs text-slate-500">UBL Customer Service Request Entry</p>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
              >
                ✕
              </button>
            </div>

            {errorMsg && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-700">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <form onSubmit={handleCreate} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">
                    Complaint Number (HERE4U)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 130631"
                    className="w-full border border-slate-200 rounded-lg p-2 font-mono"
                    value={form.complaintNumber}
                    onChange={(e) => setForm({ ...form, complaintNumber: e.target.value })}
                  />
                  <span className="text-[10px] text-slate-400">Official UBL complaint ref</span>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">
                    Custom Ticket # (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Auto: TKT-2026-XXX"
                    className="w-full border border-slate-200 rounded-lg p-2 font-mono"
                    value={form.ticketNumber}
                    onChange={(e) => setForm({ ...form, ticketNumber: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Select UBL Branch *</label>
                <select
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.branchId}
                  onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                >
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      Branch {b.branchCode} - {b.branchName} ({b.city})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Work Category *</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    <option value="Branch Renovation & Civil Works">Branch Renovation &amp; Civil Works</option>
                    <option value="Tile Flooring & Screed">Tile Flooring &amp; Screed</option>
                    <option value="False Ceiling & Painting">False Ceiling &amp; Painting</option>
                    <option value="Cash Counter Remodeling">Cash Counter Remodeling</option>
                    <option value="Electrical & Power Wiring">Electrical &amp; Power Wiring</option>
                    <option value="ATM Vestibule & Glass Facade">ATM Vestibule &amp; Glass Facade</option>
                    <option value="HVAC / Air Conditioning">HVAC / Air Conditioning</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Priority</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  >
                    <option value="LOW">Low Priority</option>
                    <option value="MEDIUM">Medium Priority</option>
                    <option value="HIGH">High Priority</option>
                    <option value="URGENT">Emergency / Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Subject / Work Description *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Branch Renovation, Cash Counter Remodeling & Tile Flooring"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Detailed Technical Scope</label>
                <textarea
                  rows={3}
                  placeholder="Specify scope, location within branch, timing constraints..."
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Service Provider / Vendor</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.vendorId}
                    onChange={(e) => setForm({ ...form, vendorId: e.target.value })}
                  >
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name} ({v.contactPerson})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Target Completion Date</label>
                  <input
                    type="date"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.targetDate}
                    onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold cursor-pointer"
                >
                  Create Ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Ticket Modal */}
      {showEditModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">Edit Ticket: {editForm.ticketNumber}</h3>
                <p className="text-xs text-slate-500">Update ticket details, vendor assignment, and scope</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
              >
                ✕
              </button>
            </div>

            {errorMsg && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-700">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <form onSubmit={handleEdit} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Complaint Number</label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded-lg p-2 font-mono"
                    value={editForm.complaintNumber}
                    onChange={(e) => setEditForm({ ...editForm, complaintNumber: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Status</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    {statuses.map((s) => (
                      <option key={s} value={s}>
                        {s.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">UBL Branch</label>
                <select
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={editForm.branchId}
                  onChange={(e) => setEditForm({ ...editForm, branchId: e.target.value })}
                >
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      Branch {b.branchCode} - {b.branchName} ({b.city})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Work Category</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  >
                    <option value="Branch Renovation & Civil Works">Branch Renovation &amp; Civil Works</option>
                    <option value="Tile Flooring & Screed">Tile Flooring &amp; Screed</option>
                    <option value="False Ceiling & Painting">False Ceiling &amp; Painting</option>
                    <option value="Cash Counter Remodeling">Cash Counter Remodeling</option>
                    <option value="Electrical & Power Wiring">Electrical &amp; Power Wiring</option>
                    <option value="ATM Vestibule & Glass Facade">ATM Vestibule &amp; Glass Facade</option>
                    <option value="HVAC / Air Conditioning">HVAC / Air Conditioning</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Priority</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={editForm.priority}
                    onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                  >
                    <option value="LOW">Low Priority</option>
                    <option value="MEDIUM">Medium Priority</option>
                    <option value="HIGH">High Priority</option>
                    <option value="URGENT">Emergency / Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Subject *</label>
                <input
                  type="text"
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={editForm.subject}
                  onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Technical Scope</label>
                <textarea
                  rows={3}
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Service Provider / Vendor</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={editForm.vendorId}
                    onChange={(e) => setEditForm({ ...editForm, vendorId: e.target.value })}
                  >
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name} ({v.contactPerson})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Assigned Site Engineer</label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={editForm.assignedTo}
                    onChange={(e) => setEditForm({ ...editForm, assignedTo: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold cursor-pointer"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
